// MathRenderer.jsx
// Place in src/components/MathRenderer.jsx
//
// Renders chat text that may contain LaTeX math AND light markdown.
//
// MATH:
//   $$...$$ — block/display math (centred, larger)
//   $...$   — inline math (within a sentence)
//   \$      — a literal dollar sign
//
// CURRENCY vs MATH: a lone "$" is very common in ordinary prose ("$43 376 in
// 2025, $38 626 so far"). A "$" only opens/closes INLINE math under the
// standard (Pandoc) rules:
//   1. the opening "$" is immediately followed by a non-space character
//   2. the closing "$" is immediately preceded by a non-space character
//   3. the closing "$" is NOT immediately followed by a digit
//   4. the span never contains another unescaped "$" or crosses a blank line
//
// MARKDOWN (applied to the plain-text parts only, never inside math):
//   **bold**, *italic*, ***both***   -> real <strong>/<em> instead of showing
//                                       the asterisks
//   # Heading                        -> rendered bold
//   * item (line start)              -> "• item"
// Anything that doesn't form a complete pair (e.g. "5 * 3", a stray "**")
// stays as literal text. Pass markdown={false} to switch this off, e.g. for
// the user's own message bubbles.
//
// NOTE: deliberately no regex lookbehind anywhere in this file — iOS Safari
// older than 16.4 cannot parse it, and one unsupported regex literal breaks
// the entire JS bundle, not just this component.
//
// Usage:
//   import MathRenderer from '../components/MathRenderer';
//   <MathRenderer text={message.content} />
//   <MathRenderer text={userMessage} markdown={false} />
//
// Requires:
//   npm install katex react-katex
//   Import KaTeX CSS once in App.js or index.js:
//   import 'katex/dist/katex.min.css';

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

function isSpace(ch) {
  return ch === undefined || /\s/.test(ch);
}

function isDigit(ch) {
  return ch !== undefined && ch >= '0' && ch <= '9';
}

// Finds the index of a valid closing "$" for an inline-math span whose
// content starts at `from`, or -1 if there isn't one.
//
// Inline math may not contain another unescaped "$". So the FIRST unescaped
// "$" reached must be a valid closer; if it isn't (space before it, or a
// digit after it), the opening "$" was really a currency sign and we stop —
// we do NOT skip ahead and pair it with some later "$". That is what keeps
// "costs $5 and the formula $E=mc^2$" from swallowing the text in between.
function findInlineClose(text, from) {
  for (let j = from; j < text.length; j++) {
    const ch = text[j];
    // A blank line ends the search — inline math never spans paragraphs.
    if (ch === '\n' && text[j + 1] === '\n') return -1;
    if (ch !== '$') continue;
    if (text[j - 1] === '\\') continue;      // escaped \$ inside the math is fine
    if (text[j + 1] === '$') return -1;      // "$$" starts a block — give up here
    if (isSpace(text[j - 1])) return -1;     // closing $ must follow a non-space
    if (isDigit(text[j + 1])) return -1;     // closing $ must not precede a digit
    return j;
  }
  return -1;
}

// ---- Parse text into segments of plain text and LaTeX ----
// Returns an array of { type: 'text'|'block'|'inline', content: string }
function parseLatex(text) {
  if (!text || typeof text !== 'string') return [{ type: 'text', content: '' }];

  const segments = [];
  let plain = '';
  let i = 0;

  const flushPlain = () => {
    if (plain) {
      segments.push({ type: 'text', content: plain });
      plain = '';
    }
  };

  while (i < text.length) {
    const ch = text[i];

    if (ch === '$') {
      // Escaped dollar: "\$" -> a literal "$" (drop the backslash)
      if (text[i - 1] === '\\' && plain.endsWith('\\')) {
        plain = plain.slice(0, -1) + '$';
        i++;
        continue;
      }

      // ---- Block math: $$...$$ ----
      if (text[i + 1] === '$') {
        const end = text.indexOf('$$', i + 2);
        if (end !== -1) {
          flushPlain();
          segments.push({ type: 'block', content: text.substring(i + 2, end).trim() });
          i = end + 2;
          continue;
        }
        // Unmatched "$$" — keep it as literal text
        plain += '$$';
        i += 2;
        continue;
      }

      // ---- Inline math: $...$ (only under the currency-safe rules above) ----
      if (!isSpace(text[i + 1])) {
        const close = findInlineClose(text, i + 1);
        if (close !== -1) {
          flushPlain();
          segments.push({ type: 'inline', content: text.substring(i + 1, close).trim() });
          i = close + 1;
          continue;
        }
      }
      // Otherwise this "$" is just a dollar sign — fall through as plain text.
    }

    plain += ch;
    i++;
  }

  flushPlain();
  return segments.length ? segments : [{ type: 'text', content: '' }];
}

// ---- Light markdown: line-level pass ("# Heading", "* bullet") ----
// atLineStart is false when this text segment begins mid-line (right after an
// inline-math span), in which case its first line is left alone.
function normalizeBlockMarkdown(text, atLineStart) {
  return text
    .split('\n')
    .map((line, k) => {
      if (k === 0 && !atLineStart) return line;
      // "## Title" (optionally "## Title ##") -> bold line
      const h = line.match(/^[ \t]*#{1,6}[ \t]+(.+?)(?:[ \t]+#+)?[ \t]*$/);
      if (h) {
        const inner = h[1];
        return /^\*\*.+\*\*$/.test(inner) ? inner : '**' + inner + '**';
      }
      // "* item" -> "• item"
      return line.replace(/^([ \t]*)\*[ \t]+/, '$1• ');
    })
    .join('\n');
}

// ---- Light markdown: inline emphasis ----
// Finds the closing marker for an emphasis span whose content starts at
// `from`, or -1. Closer must follow a non-space; the span never crosses a
// blank line; a single "*" closer must not be part of a "**" run.
function findEmphasisClose(text, from, marker) {
  for (let j = from; j < text.length; j++) {
    if (text[j] === '\n' && text[j + 1] === '\n') return -1;
    if (text[j] !== '*' || j === from) continue;
    if (marker.length === 1) {
      if (text[j - 1] === '*' || text[j + 1] === '*') continue;
    } else if (!text.startsWith(marker, j)) {
      continue;
    }
    if (isSpace(text[j - 1])) continue;
    return j;
  }
  return -1;
}

// Returns an array of strings and { t: 'strong'|'em'|'both', c: [...] } nodes.
function parseInlineMarkdown(text) {
  const nodes = [];
  let plain = '';
  let i = 0;

  const flush = () => {
    if (plain) {
      nodes.push(plain);
      plain = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '*') {
      const marker = text.startsWith('***', i) ? '***' : text.startsWith('**', i) ? '**' : '*';
      const after = text[i + marker.length];
      // Opener must be followed by a non-space, non-asterisk character
      if (!isSpace(after) && after !== '*') {
        const close = findEmphasisClose(text, i + marker.length, marker);
        if (close !== -1) {
          flush();
          nodes.push({
            t: marker === '***' ? 'both' : marker === '**' ? 'strong' : 'em',
            c: parseInlineMarkdown(text.substring(i + marker.length, close)),
          });
          i = close + marker.length;
          continue;
        }
      }
      // No complete pair — the asterisk(s) are just literal text
      plain += marker;
      i += marker.length;
      continue;
    }
    plain += text[i];
    i++;
  }

  flush();
  return nodes;
}

// ---- Render a single segment ----
function renderNodes(nodes, keyBase) {
  return nodes.map((n, idx) => {
    if (typeof n === 'string') return n;
    const key = `${keyBase}-${idx}`;
    const kids = renderNodes(n.c, key);
    if (n.t === 'strong') return <strong key={key}>{kids}</strong>;
    if (n.t === 'em') return <em key={key}>{kids}</em>;
    return <strong key={key}><em>{kids}</em></strong>;
  });
}

function RichText({ text, atLineStart, markdown }) {
  if (!markdown) return text;
  return renderNodes(parseInlineMarkdown(normalizeBlockMarkdown(text, atLineStart)), 'md');
}

function Segment({ segment, atLineStart, markdown }) {
  if (segment.type === 'block') {
    return (
      <div style={{ margin: '8px 0', overflowX: 'auto' }}>
        <BlockMath
          math={segment.content}
          renderError={(error) => (
            <span style={{ color: '#c62828', fontSize: '12px' }}>
              [{segment.content}]
            </span>
          )}
        />
      </div>
    );
  }

  if (segment.type === 'inline') {
    return (
      <InlineMath
        math={segment.content}
        renderError={() => (
          <span style={{ fontFamily: 'monospace' }}>${segment.content}$</span>
        )}
      />
    );
  }

  // Plain text — preserve whitespace and line breaks
  return (
     <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      <RichText text={segment.content} atLineStart={atLineStart} markdown={markdown} />
    </span>
  );
}

// ---- Main component ----
export default function MathRenderer({ text, style, markdown = true }) {
  const segments = parseLatex(text);

  // If no LaTeX was found — render directly for performance.
  // (Render the PARSED text, not the raw prop, so an escaped "\$" shows as "$".)
  const hasLatex = segments.some(s => s.type === 'block' || s.type === 'inline');
  if (!hasLatex) {
    return (
      <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...style }}>
        <RichText
          text={segments.map(s => s.content).join('')}
          atLineStart={true}
          markdown={markdown}
        />
      </span>
    );
  }

  return (
    <span style={{ overflowWrap: 'anywhere', ...style }}>
      {segments.map((segment, i) => (
        <Segment
          key={i}
          segment={segment}
          atLineStart={i === 0 || segments[i - 1].type === 'block'}
          markdown={markdown}
        />
      ))}
    </span>
  );
}
//////////////////////////////////////////////////////////////////////////////////////////////
// // MathRenderer.jsx
// // Place in src/components/MathRenderer.jsx
// //
// // Renders text that may contain LaTeX mathematical notation.
// // Supports:
// //   $$...$$ — block/display math (centred, larger)
// //   $...$   — inline math (within a sentence)
// //   \$      — a literal dollar sign
// //   Plain text — rendered as-is
// //
// // CURRENCY vs MATH: a lone "$" is very common in ordinary prose ("$43 376 in
// // 2025, $38 626 so far"). The old parser paired ANY two "$" characters, which
// // turned everything between two currency amounts into garbled math. A "$"
// // now only opens/closes INLINE math under the standard (Pandoc) rules:
// //   1. the opening "$" is immediately followed by a non-space character
// //   2. the closing "$" is immediately preceded by a non-space character
// //   3. the closing "$" is NOT immediately followed by a digit
// //   4. the span never crosses a blank line (a paragraph break)
// // "$x^2 + 1$" is still math. "$43 376 ... $38 626" is not (the second "$" has
// // a space before it), so both amounts stay literal text.
// //
// // NOTE: deliberately no regex lookbehind anywhere in this file — iOS Safari
// // older than 16.4 cannot parse it, and one unsupported regex literal breaks
// // the entire JS bundle, not just this component.
// //
// // Usage:
// //   import MathRenderer from '../components/MathRenderer';
// //   <MathRenderer text={message.content} />
// //
// // Requires:
// //   npm install katex react-katex
// //   Import KaTeX CSS once in App.js or index.js:
// //   import 'katex/dist/katex.min.css';

// import React from 'react';
// import { InlineMath, BlockMath } from 'react-katex';
// import 'katex/dist/katex.min.css';

// function isSpace(ch) {
//   return ch === undefined || /\s/.test(ch);
// }

// function isDigit(ch) {
//   return ch !== undefined && ch >= '0' && ch <= '9';
// }

// // Finds the index of a valid closing "$" for an inline-math span whose
// // content starts at `from`, or -1 if there isn't one.
// //
// // Inline math may not contain another unescaped "$". So the FIRST unescaped
// // "$" reached must be a valid closer; if it isn't (space before it, or a
// // digit after it), the opening "$" was really a currency sign and we stop —
// // we do NOT skip ahead and pair it with some later "$". That is what keeps
// // "costs $5 and the formula $E=mc^2$" from swallowing the text in between.
// function findInlineClose(text, from) {
//   for (let j = from; j < text.length; j++) {
//     const ch = text[j];
//     // A blank line ends the search — inline math never spans paragraphs.
//     if (ch === '\n' && text[j + 1] === '\n') return -1;
//     if (ch !== '$') continue;
//     if (text[j - 1] === '\\') continue;      // escaped \$ inside the math is fine
//     if (text[j + 1] === '$') return -1;      // "$$" starts a block — give up here
//     if (isSpace(text[j - 1])) return -1;     // closing $ must follow a non-space
//     if (isDigit(text[j + 1])) return -1;     // closing $ must not precede a digit
//     return j;
//   }
//   return -1;
// }

// // ---- Parse text into segments of plain text and LaTeX ----
// // Returns an array of { type: 'text'|'block'|'inline', content: string }
// function parseLatex(text) {
//   if (!text || typeof text !== 'string') return [{ type: 'text', content: '' }];

//   const segments = [];
//   let plain = '';
//   let i = 0;

//   const flushPlain = () => {
//     if (plain) {
//       segments.push({ type: 'text', content: plain });
//       plain = '';
//     }
//   };

//   while (i < text.length) {
//     const ch = text[i];

//     if (ch === '$') {
//       // Escaped dollar: "\$" -> a literal "$" (drop the backslash)
//       if (text[i - 1] === '\\' && plain.endsWith('\\')) {
//         plain = plain.slice(0, -1) + '$';
//         i++;
//         continue;
//       }

//       // ---- Block math: $$...$$ ----
//       if (text[i + 1] === '$') {
//         const end = text.indexOf('$$', i + 2);
//         if (end !== -1) {
//           flushPlain();
//           segments.push({ type: 'block', content: text.substring(i + 2, end).trim() });
//           i = end + 2;
//           continue;
//         }
//         // Unmatched "$$" — keep it as literal text
//         plain += '$$';
//         i += 2;
//         continue;
//       }

//       // ---- Inline math: $...$ (only under the currency-safe rules above) ----
//       if (!isSpace(text[i + 1])) {
//         const close = findInlineClose(text, i + 1);
//         if (close !== -1) {
//           flushPlain();
//           segments.push({ type: 'inline', content: text.substring(i + 1, close).trim() });
//           i = close + 1;
//           continue;
//         }
//       }
//       // Otherwise this "$" is just a dollar sign — fall through as plain text.
//     }

//     plain += ch;
//     i++;
//   }

//   flushPlain();
//   return segments.length ? segments : [{ type: 'text', content: '' }];
// }

// // ---- Render a single segment ----
// function Segment({ segment }) {
//   if (segment.type === 'block') {
//     return (
//       <div style={{ margin: '8px 0', overflowX: 'auto' }}>
//         <BlockMath
//           math={segment.content}
//           renderError={(error) => (
//             <span style={{ color: '#c62828', fontSize: '12px' }}>
//               [{segment.content}]
//             </span>
//           )}
//         />
//       </div>
//     );
//   }

//   if (segment.type === 'inline') {
//     return (
//       <InlineMath
//         math={segment.content}
//         renderError={() => (
//           <span style={{ fontFamily: 'monospace' }}>${segment.content}$</span>
//         )}
//       />
//     );
//   }

//   // Plain text — preserve whitespace and line breaks
//   return (
//     <span style={{ whiteSpace: 'pre-wrap' }}>
//       {segment.content}
//     </span>
//   );
// }

// // ---- Main component ----
// export default function MathRenderer({ text, style }) {
//   const segments = parseLatex(text);

//   // If no LaTeX was found — render plain text directly for performance.
//   // (Render the PARSED text, not the raw prop, so an escaped "\$" shows as "$".)
//   const hasLatex = segments.some(s => s.type === 'block' || s.type === 'inline');
//   if (!hasLatex) {
//     return (
//       <span style={{ whiteSpace: 'pre-wrap', ...style }}>
//         {segments.map(s => s.content).join('')}
//       </span>
//     );
//   }

//   return (
//     <span style={style}>
//       {segments.map((segment, i) => (
//         <Segment key={i} segment={segment} />
//       ))}
//     </span>
//   );
// }